import MuiLink from "@mui/material/Link";
import { Link as RouterLink } from "react-router";
import { cleanProps } from "../internal/cleanProps";
import { tokens } from "../tokens";
import type { LinkProps } from "../types";

/**
 * Containment note (types.ts): upstream `MuiLink` is
 * `OverridableComponent<LinkTypeMap>`. This adapter fixes `RootComponent`
 * permanently to react-router's own `Link` — there is no `as`/`component`
 * prop on `LinkProps`, so a caller can never choose a different root
 * element — and fixes `AdditionalProps` to `{}`. `to` is react-router's own
 * navigation target, consumed through `history.pushState` by the router,
 * never assigned to `location.href` or any other raw DOM attribute
 * (ADR-20260821-ny4x's elevation concern does not apply the same way here:
 * this prop IS the navigation target, by design, not an incidental
 * passthrough of one).
 *
 * ADR-20261008-hsvr: "a text link that navigates without full page
 * reload" — exactly what wrapping react-router's `Link` (rather than a
 * plain anchor) gives: a client-side `history.pushState`, not a
 * full-document navigation.
 */
export function Link({ to, children, testId }: Readonly<LinkProps>) {
  return (
    <MuiLink
      component={RouterLink}
      to={to}
      // H2 01-PortalEntry: an inline text link, 1px underline at 0.2em, darker and 2px on hover.
      sx={{
        color: tokens.colour.brand[700],
        textDecoration: "underline",
        textUnderlineOffset: "0.2em",
        textDecorationThickness: "1px",
        "&:hover": { color: tokens.colour.brand[900], textDecorationThickness: "2px" },
      }}
      {...cleanProps({ "data-testid": testId })}
    >
      {children}
    </MuiLink>
  );
}
